import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

import { ENTITY_TYPE } from "../../constants";
import { ENTITY_TYPE_LABELS } from "../entities/entityLabels";
import {
  DEFAULT_ENTITY_CONFIG,
  allEntityDocs,
  getEntityConfig,
  saveEntityConfig,
} from "../../services/backend/EntityConfigService";

// Configuración del módulo de ENTIDADES — la fuente de verdad (config/entities)
// de qué documentos se le exigen a cada tipo de entidad, los sectores, el bucket
// de la documentación y el registro público.
//
// Vive en la pantalla general de configuración y no dentro de Convenios: una
// entidad no le pertenece a Convenios. Hoy la consume ese módulo y mañana la
// consumirá Contratos, así que su configuración no puede colgar de ninguno de
// los dos.
//
// Cerrada al ROL de ADMIN, no a un permiso: los permisos son una foto por
// usuario, así que un permiso nuevo no existiría en las cuentas ya creadas
// (mismo criterio que FiledConfig y ApplicantConfig).

const ENTITY_TYPE_VALUES = Object.values(ENTITY_TYPE);

const blankDoc = () => ({
  key: "",
  label: "",
  required: true,
  enabled: true,
  minFiles: 1,
  maxFiles: 1,
  order: 0,
  acceptedMime: ["application/pdf", "image/*"],
  entityTypes: [],
  hasExpiry: false,
  expiryWarningDays: 30,
  description: "",
  aiHints: "",
  keywords: [],
  extra: {},
});

const toList = (s) => String(s || "").split(",").map((x) => x.trim()).filter(Boolean);
const toLines = (arr) => (Array.isArray(arr) ? arr.join("\n") : "");
const fromLines = (s) => String(s || "").split("\n").map((x) => x.trim()).filter(Boolean);

export default function EntityConfig() {
  const user = useSelector((state) => state.user.value);
  const isAdmin = !!(user?.roles || []).includes("ADMIN");

  const [cfg, setCfg] = useState(DEFAULT_ENTITY_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [tab, setTab] = useState("documents");
  const [dialog, setDialog] = useState(null); // { mode: 'create'|'edit', origKey, doc }

  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      setCfg(await getEntityConfig());
      setLoading(false);
    })();
  }, [isAdmin]);

  const catalog = useMemo(() => allEntityDocs(cfg), [cfg]);

  const persist = async (patch) => {
    setSaving(true);
    const ok = await saveEntityConfig(patch);
    setSaving(false);
    setFeedback(ok
      ? { severity: "success", msg: "Configuración guardada." }
      : { severity: "error", msg: "No fue posible guardar la configuración." });
    return ok;
  };

  const saveCatalog = (next) => {
    setCfg((c) => ({ ...c, entityDocumentCatalog: next }));
    return persist({ entityDocumentCatalog: next });
  };

  const handleSaveDoc = async (docDef) => {
    // Unicidad por key — el documento que se edita se identifica por su clave
    // ORIGINAL, nunca por su posición: las tarjetas pintan una copia ordenada.
    const key = (docDef.key || "").trim();
    if (!key) { setFeedback({ severity: "warning", msg: "La clave (key) es obligatoria." }); return; }
    const list = cfg.entityDocumentCatalog || [];
    const origKey = dialog.mode === "edit" ? dialog.origKey : null;
    if (list.some((d) => d.key === key && d.key !== origKey)) {
      setFeedback({ severity: "warning", msg: `Ya existe un documento con la clave "${key}".` });
      return;
    }
    const clean = { ...docDef, key };
    const next = dialog.mode === "create"
      ? [...list, clean]
      : list.map((d) => (d.key === origKey ? clean : d));
    if (await saveCatalog(next)) setDialog(null);
  };

  const handleDeleteDoc = async (docKey) => {
    if (!window.confirm(
      `¿Eliminar el documento "${docKey}"?\n\nLos archivos ya cargados por las entidades no se borran, pero dejarán de pedirse.`
    )) return;
    await saveCatalog((cfg.entityDocumentCatalog || []).filter((d) => d.key !== docKey));
  };

  if (!isAdmin) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6">Sin acceso</Typography>
        <Typography variant="body2" color="text.secondary">
          Sólo un administrador puede configurar el módulo de Entidades.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 3 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Define qué documentos debe entregar cada tipo de entidad, los sectores disponibles y cómo
        funciona el registro público. Lo usan hoy los convenios y mañana los contratos.
      </Typography>

      {feedback && (
        <Alert severity={feedback.severity} sx={{ mb: 2 }} onClose={() => setFeedback(null)}>
          {feedback.msg}
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}>
        <Tab value="documents" label="Documentos" />
        <Tab value="registro" label="Registro público" />
        <Tab value="general" label="Sectores y almacenamiento" />
      </Tabs>

      {loading ? (
        <Typography>Cargando…</Typography>
      ) : (
        <>
          {tab === "documents" && (
            <DocumentsTab
              catalog={catalog}
              onCreate={() => setDialog({ mode: "create", doc: blankDoc() })}
              onEdit={(d) => setDialog({ mode: "edit", origKey: d.key, doc: d })}
              onDelete={handleDeleteDoc}
            />
          )}
          {tab === "registro" && (
            <PublicRegistrationTab cfg={cfg} setCfg={setCfg} onSave={persist} saving={saving} />
          )}
          {tab === "general" && (
            <GeneralTab cfg={cfg} setCfg={setCfg} onSave={persist} saving={saving} />
          )}
        </>
      )}

      {dialog && (
        <DocDialog
          mode={dialog.mode}
          initial={dialog.doc}
          saving={saving}
          onClose={() => setDialog(null)}
          onSave={handleSaveDoc}
        />
      )}
    </Box>
  );
}

function DocumentsTab({ catalog, onCreate, onEdit, onDelete }) {
  return (
    <>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="h6">Documentos de entidades</Typography>
        <Button startIcon={<AddIcon />} variant="outlined" onClick={onCreate}>Nuevo documento</Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Cada documento declara <strong>a qué tipos de entidad</strong> se le exige: una alcaldía no
        tiene cámara de comercio y una empresa privada no tiene acto administrativo de creación. Un
        documento <strong>sin tipos marcados se le pide a todas</strong>.
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Marcar <strong>«Tiene vigencia»</strong> hace que se pida la fecha hasta la que sirve el
        documento. «Vencido» no se guarda en ninguna parte: se calcula contra la fecha de hoy, así
        que un papel se vence solo el día que le toca.
      </Typography>

      {catalog.length === 0 ? (
        <Alert severity="info">Aún no hay documentos. Crea al menos uno.</Alert>
      ) : (
        <>
          {/* Los dos grupos son la MISMA lista partida por «Tiene vigencia»: no
              hay dos catálogos. Se separan porque son dos compromisos distintos
              para la entidad — uno se entrega y se acabó, el otro hay que
              renovarlo cada tanto — y en una sola rejilla no se distinguían. */}
          {/* «Permanentes» es respecto del BARRIDO: son los que no tienen fecha
              de vencimiento que avisar. Uno puede pedir fecha de expedición y
              aun así estar aquí (el RUT), porque cuánto sirve esa expedición lo
              decide el módulo que lo consume, no este catálogo. */}
          <CatalogGroup
            title="Permanentes"
            caption="Sin fecha de vencimiento que vigilar: quedan en la ficha de la entidad hasta que alguien los reemplace."
            docs={catalog.filter((d) => !d.hasExpiry)}
            onEdit={onEdit}
            onDelete={onDelete}
          />
          <CatalogGroup
            title="Renovables"
            caption="Tienen vigencia. El barrido diario le avisa por correo a la entidad antes de que se venzan, para que cargue la versión nueva."
            docs={catalog.filter((d) => d.hasExpiry)}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        </>
      )}
    </>
  );
}

function CatalogGroup({ title, caption, docs, onEdit, onDelete }) {
  if (docs.length === 0) return null;
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
        {caption}
      </Typography>
        <Grid container spacing={2}>
          {docs.map((d) => {
            const types = (d.entityTypes || []).filter(Boolean);
            return (
              <Grid item xs={12} md={6} key={d.key}>
                <Card variant="outlined" sx={{ height: "100%", opacity: d.enabled === false ? 0.55 : 1 }}>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight={600}>{d.label || d.key}</Typography>
                        <Typography variant="caption" color="text.secondary">{d.key}</Typography>
                      </Box>
                      <Box sx={{ flexShrink: 0 }}>
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => onEdit(d)}><EditIcon fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" onClick={() => onDelete(d.key)}><DeleteIcon fontSize="small" /></IconButton>
                        </Tooltip>
                      </Box>
                    </Stack>
                    <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: "wrap", gap: 0.5 }}>
                      <Chip size="small" color={d.required === false ? "default" : "primary"}
                        label={d.required === false ? "Opcional" : "Obligatorio"} />
                      <Chip size="small" variant="outlined"
                        label={Number(d.maxFiles) === 0 ? "Archivos ilimitados" : `Hasta ${d.maxFiles || 1} archivo(s)`} />
                      {d.hasExpiry && (
                        <Chip size="small" variant="outlined" color="warning"
                          label={`Vigencia · avisa ${d.expiryWarningDays || 30} d antes`} />
                      )}
                      {d.hasIssueDate && (
                        <Chip size="small" variant="outlined" label="Pide fecha de expedición" />
                      )}
                      {d.enabled === false && <Chip size="small" label="Deshabilitado" />}
                    </Stack>
                    <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: "wrap", gap: 0.5 }}>
                      {types.length === 0 ? (
                        <Chip size="small" variant="outlined" label="Todas las entidades" />
                      ) : (
                        types.map((t) => (
                          <Chip key={t} size="small" variant="outlined" label={ENTITY_TYPE_LABELS[t] || t} />
                        ))
                      )}
                    </Stack>
                    {d.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{d.description}</Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
    </Box>
  );
}

function DocDialog({ mode, initial, saving, onClose, onSave }) {
  const [d, setD] = useState({ ...blankDoc(), ...initial });
  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{mode === "create" ? "Nuevo documento" : "Editar documento"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Clave (key)" value={d.key} onChange={(e) => set("key", e.target.value.trim())}
            fullWidth helperText="Identificador estable, sin espacios (p. ej. chamber_of_commerce). Se guarda como type del documento." />
          <TextField label="Etiqueta (visible)" value={d.label} onChange={(e) => set("label", e.target.value)} fullWidth />

          <FormControl fullWidth>
            <InputLabel id="entity-types-label">Se le exige a</InputLabel>
            <Select
              labelId="entity-types-label"
              multiple
              value={(d.entityTypes || []).filter((t) => ENTITY_TYPE_VALUES.includes(t))}
              onChange={(e) => set("entityTypes", typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value)}
              input={<OutlinedInput label="Se le exige a" />}
              renderValue={(selected) =>
                selected.length === 0
                  ? "Todas las entidades"
                  : selected.map((t) => ENTITY_TYPE_LABELS[t] || t).join(", ")}
            >
              {ENTITY_TYPE_VALUES.map((t) => (
                <MenuItem key={t} value={t}>
                  <Checkbox checked={(d.entityTypes || []).indexOf(t) > -1} />
                  <ListItemText primary={ENTITY_TYPE_LABELS[t] || t} />
                </MenuItem>
              ))}
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              Vacío = se le pide a toda entidad, sea del tipo que sea.
            </Typography>
          </FormControl>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField type="number" fullWidth label="Orden" value={d.order ?? 0}
                onChange={(e) => set("order", Number(e.target.value))} inputProps={{ step: 1 }} />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={<Switch checked={Number(d.maxFiles) === 0} onChange={(e) => set("maxFiles", e.target.checked ? 0 : 1)} />}
                label="Ilimitados"
              />
              {Number(d.maxFiles) !== 0 && (
                <TextField type="number" fullWidth label="Máx. archivos" value={d.maxFiles ?? 1}
                  onChange={(e) => set("maxFiles", Math.max(1, Number(e.target.value)))} inputProps={{ min: 1, step: 1 }} />
              )}
            </Grid>
          </Grid>

          <Stack direction="row" spacing={2} alignItems="center">
            <FormControlLabel
              control={<Switch checked={!!d.hasExpiry} onChange={(e) => set("hasExpiry", e.target.checked)} />}
              label="Tiene vigencia" />
            {d.hasExpiry && (
              <TextField type="number" label="Avisar (días antes)" value={d.expiryWarningDays ?? 30}
                onChange={(e) => set("expiryWarningDays", Math.max(0, Number(e.target.value)))}
                inputProps={{ min: 0, step: 1 }} sx={{ width: 180 }} />
            )}
          </Stack>

          {/* Interruptor aparte de «Tiene vigencia», no una variante suya: son
              dos fechas distintas y un documento puede exigir las dos. La de
              vencimiento la DECLARA quien carga ("vigente hasta"); la de
              expedición es un hecho del papel, y quien decide cuánto sirve es el
              módulo que lo consume (el RUT: 3 meses, en la solicitud de pago). */}
          <FormControlLabel
            control={<Switch checked={!!d.hasIssueDate}
              onChange={(e) => set("hasIssueDate", e.target.checked)} />}
            label="Pide fecha de expedición" />
          <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5 }}>
            Se le pedirá al cargar el documento. Actívalo cuando su vigencia se cuente desde
            que lo expidieron y no desde una fecha que declare quien lo sube.
          </Typography>

          <TextField label="Tipos MIME aceptados" value={(d.acceptedMime || []).join(", ")}
            onChange={(e) => set("acceptedMime", toList(e.target.value))} fullWidth
            helperText="Separados por coma. P. ej. application/pdf, image/*" />
          <TextField label="Descripción (contexto humano + IA)" value={d.description}
            onChange={(e) => set("description", e.target.value)} fullWidth multiline minRows={2} />
          <TextField label="Pistas para IA" value={d.aiHints} onChange={(e) => set("aiHints", e.target.value)}
            fullWidth multiline minRows={2}
            helperText="Qué debería contener el documento / cómo validarlo automáticamente." />
          <TextField label="Palabras clave" value={(d.keywords || []).join(", ")}
            onChange={(e) => set("keywords", toList(e.target.value))} fullWidth
            helperText="Sinónimos / frases esperadas, separadas por coma." />

          <Stack direction="row" spacing={2}>
            <FormControlLabel control={<Checkbox checked={d.required !== false}
              onChange={(e) => set("required", e.target.checked)} />} label="Obligatorio" />
            <FormControlLabel control={<Switch checked={d.enabled !== false}
              onChange={(e) => set("enabled", e.target.checked)} />} label="Habilitado" />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" onClick={() => onSave(d)} disabled={saving}>
          {saving ? "Guardando…" : "Guardar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Sectores y bucket en una sola pestaña: son los dos ajustes sueltos que no
// pertenecen ni al catálogo ni al portal público, y cada uno solo no llenaba
// una pestaña.
function GeneralTab({ cfg, setCfg, onSave, saving }) {
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>Sectores</Typography>
        <TextField label="Sectores" value={toLines(cfg.sectors)}
          onChange={(e) => set("sectors", fromLines(e.target.value))} fullWidth multiline minRows={8}
          helperText="Uno por línea. Es la lista que ofrece el formulario de la entidad." />

        <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
          Documento del representante legal
        </Typography>
        <TextField label="Tipos de documento de identidad" value={toLines(cfg.idTypes)}
          onChange={(e) => set("idTypes", fromLines(e.target.value))} fullWidth multiline minRows={4}
          helperText="Uno por línea. Es la lista que ofrece el selector del representante legal, tanto en el portal público como en la ficha." />

        <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>Almacenamiento</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Destino donde se guardan los documentos de las entidades. Es la fuente de verdad para el
          backend: si se deja en blanco, se usa el bucket por defecto del proyecto.
        </Typography>
        <TextField label="Bucket de almacenamiento (GCS)" value={cfg.storageBucket || ""}
          onChange={(e) => set("storageBucket", e.target.value.trim())} fullWidth />

        <Box sx={{ mt: 2, textAlign: "right" }}>
          <Button variant="contained" disabled={saving}
            onClick={() => onSave({
              sectors: cfg.sectors,
              idTypes: cfg.idTypes,
              storageBucket: cfg.storageBucket,
            })}>
            {saving ? "Guardando…" : "Guardar"}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}

function PublicRegistrationTab({ cfg, setCfg, onSave, saving }) {
  const mb = Math.round((Number(cfg.entityMaxFileBytes) || 0) / 1024 / 1024) || 10;
  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>Registro público</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          La pestaña «Empresas y entidades» de <code>/submission</code>, por donde una entidad se
          registra sola y mantiene su documentación al día. Lo que se le pide sale del catálogo de
          esta misma pantalla.
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={cfg.publicRegistrationEnabled !== false}
              onChange={(e) => setCfg((c) => ({ ...c, publicRegistrationEnabled: e.target.checked }))}
            />
          }
          label="Aceptar registros nuevos"
        />
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          Apagarlo esconde el botón de registro y deja de aceptar entidades nuevas. Las que ya
          están registradas siguen entrando a actualizar sus documentos: cerrar la puerta de
          entrada no debería dejar vencer los papeles de quien ya está adentro.
        </Typography>

        <TextField
          type="number"
          label="Tamaño máximo por archivo (MB)"
          value={mb}
          onChange={(e) =>
            setCfg((c) => ({ ...c, entityMaxFileBytes: Math.max(1, Number(e.target.value)) * 1024 * 1024 }))
          }
          inputProps={{ min: 1, max: 32, step: 1 }}
          helperText="Tope duro del backend: 32 MB."
          sx={{ mb: 2, display: "block", maxWidth: 320 }}
        />

        <FormControlLabel
          control={
            <Switch
              checked={!!cfg.smsTestMode}
              onChange={(e) => setCfg((c) => ({ ...c, smsTestMode: e.target.checked }))}
            />
          }
          label="Modo de prueba de SMS"
        />
        <Typography variant="caption" color="text.secondary" display="block">
          Usa el número y el código de prueba de Firebase Phone Auth en vez de gastar un mensaje
          real. <strong>Sólo para desarrollo</strong>: con esto encendido, la verificación del
          celular no prueba nada.
        </Typography>

        <Box sx={{ mt: 2, textAlign: "right" }}>
          <Button variant="contained" disabled={saving}
            onClick={() => onSave({
              publicRegistrationEnabled: cfg.publicRegistrationEnabled !== false,
              entityMaxFileBytes: cfg.entityMaxFileBytes,
              smsTestMode: !!cfg.smsTestMode,
            })}>
            {saving ? "Guardando…" : "Guardar registro público"}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
